import { FC, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationManager } from 'react-notifications';
import { useHistory } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Column } from '@material-table/core';
import { Card } from '@tremor/react';
import moment from 'moment';
import appState from '../../state/AppStateContainer';
import { ServiceDashboard } from '../../services/service-dashboard';
import DataTable from '../../components/shared/DataTable/DataTable';
import StyledCheckbox from '../../components/shared/StyledCheckbox/StyledCheckbox';
import FloatingActionBar, { FloatingActionBarButton } from '../../components/shared/FloatingActionBar/FloatingActionBar';
import TodoCalendar from './TodoCalendar';
import { DeadlineRow, DeadlineDisplayType } from './types';
import {
  TYPE_LABELS, getDisplayType, typeDotStyle,
  urgencyStyle, urgencyTone, compareDeadlineRows,
} from './activityMeta';
import { loadFilters, saveFilters, pruneDates, toIsoDay } from './deadlineFilters';
import FilterPill from '../../components/shared/FilterPill/FilterPill';
import styles from './DeadlinesTab.module.scss';

const DaysLeftBadge: FC<{ daysLeft: number }> = ({ daysLeft }) => (
  <span className={styles.daysLeftBadge} style={urgencyStyle(urgencyTone(daysLeft))}>
    {daysLeft}
  </span>
);

// Neutral gray badge (consistent with the filter pills) — the type colour is carried only by
// the leading dot, not the whole badge.
const TypeBadge: FC<{ type: DeadlineDisplayType; label: string }> = ({ type, label }) => (
  <span className={styles.typeBadge}>
    <span className={styles.typeDot} style={typeDotStyle(type)} />
    {label}
  </span>
);

type TypeOption = { value: DeadlineDisplayType; label: string };

const DeadlinesTab: FC = () => {
  const [t] = useTranslation('dashboard');
  const history = useHistory();
  const orgUrl = appState._getCurrentOrgUrl();
  const [rows, setRows] = useState<DeadlineRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Distinguishes "the request failed" from "there genuinely are no deadlines" — an empty list is
  // otherwise indistinguishable, and the two need opposite handling when pruning date filters.
  const [loadFailed, setLoadFailed] = useState(false);
  // Active filters restore from localStorage (per user) so a reload keeps the view the user set up.
  // Row selection deliberately does not persist — it drives destructive bulk actions.
  const savedFilters = useMemo(loadFilters, []);
  const [typeFilters, setTypeFilters] = useState<Set<DeadlineDisplayType>>(() => new Set(savedFilters.types));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedDates, setSelectedDates] = useState<Set<string>>(() => new Set(savedFilters.dates));

  // Skips the mount pass: the restored value is already what is stored, so writing it back only
  // risks pruning fields this bundle does not understand before the user has touched anything.
  const settled = useRef(false);
  useEffect(() => {
    if (!settled.current) { settled.current = true; return; }
    saveFilters(typeFilters, selectedDates);
  }, [typeFilters, selectedDates]);

  // Toggle a day in/out of the multi-select date filter (clears row selection to keep bulk
  // actions in sync with the now-different visible rows).
  const toggleDate = (iso: string) => {
    setSelected(new Set());
    setSelectedDates(prev => {
      const next = new Set(prev);
      next.has(iso) ? next.delete(iso) : next.add(iso);
      return next;
    });
  };

  const clearAllFilters = () => {
    setSelected(new Set());
    setTypeFilters(new Set());
    setSelectedDates(new Set());
  };

  const loadDeadlines = useCallback(() => {
    setIsLoading(true);
    setLoadFailed(false);
    ServiceDashboard.getAllDeadlines()
      .then((data: DeadlineRow[]) => setRows(data))
      .catch(() => { setRows([]); setLoadFailed(true); })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => { loadDeadlines(); }, [loadDeadlines]);

  // Invariant: a selected day must always have at least one row behind it, so a date chip can never
  // outlive the deadline that justified it. Maintained here rather than at each mutation site, so
  // the initial load, a restored filter whose deadline has since gone, a single dismissal and a
  // bulk dismissal share one rule — and none of them reads a stale `rows` from an async handler.
  // Deliberately keyed on `rows` only: the type filter is a reversible lens, and because the save
  // effect persists whatever this produces, pruning on a type change would permanently delete date
  // selections that widening the filter again could never restore.
  // Skipped when the request failed: pruning against an empty list would silently wipe the user's
  // restored filters because of an outage. A genuinely empty result does prune, which is correct.
  useEffect(() => {
    if (isLoading || loadFailed) return;
    setSelectedDates(prev => pruneDates(prev, rows));
  }, [rows, isLoading, loadFailed]);

  const overdueCount = useMemo(() => rows.filter(r => r.daysLeft < 0).length, [rows]);
  const soonCount = useMemo(() => rows.filter(r => r.daysLeft >= 0 && r.daysLeft <= 7).length, [rows]);
  const upcomingCount = useMemo(() => rows.filter(r => r.daysLeft > 7).length, [rows]);

  const typeOptions = useMemo<TypeOption[]>(
    () => (Object.keys(TYPE_LABELS) as DeadlineDisplayType[]).map(key => ({ value: key, label: t(...TYPE_LABELS[key]) })),
    [t]
  );
  const toggleTypeFilter = (type: DeadlineDisplayType) => {
    const next = new Set(typeFilters);
    next.has(type) ? next.delete(type) : next.add(type);
    setSelected(new Set());
    setTypeFilters(next);
    // Date selections are deliberately left alone: narrowing the types can leave a date chip over
    // a temporarily empty table, and the chips themselves explain why it is empty.
  };

  // Type filter applies to BOTH the calendar and the table; the day filter only narrows the table
  // (the calendar is what sets it). So the calendar gets typeFilteredRows, the table gets those
  // further filtered by selected dates.
  const typeFilteredRows = typeFilters.size > 0 ? rows.filter(r => typeFilters.has(getDisplayType(r))) : rows;
  const visibleRows = typeFilteredRows
    .filter(r => selectedDates.size === 0 || selectedDates.has(toIsoDay(r.deadline)))
    .slice()
    .sort(compareDeadlineRows);

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected(visibleRows.length > 0 && selected.size === visibleRows.length
      ? new Set()
      : new Set(visibleRows.map(r => r.id))
    );
  };

  const handleDismiss = async (row: DeadlineRow) => {
    try {
      await ServiceDashboard.dismissDeadline(row.id);
      setRows(prev => prev.filter(r => r.id !== row.id));
      setSelected(prev => { const next = new Set(prev); next.delete(row.id); return next; });
    } catch {
      NotificationManager.error(
        t('notification.error.unexpected', 'Något gick fel, vänligen försök igen'),
        t('notification.error.delete.title', 'Misslyckat')
      );
    }
  };

  const handleBulkDismiss = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      await ServiceDashboard.dismissDeadlines(ids);
      const dismissed = new Set(ids);
      setRows(prev => prev.filter(r => !dismissed.has(r.id)));
      setSelected(new Set());
    } catch {
      NotificationManager.error(
        t('notification.error.unexpected', 'Något gick fel, vänligen försök igen'),
        t('notification.error.delete.title', 'Misslyckat')
      );
    }
  }, [selected, t]);

  const getTypeLabel = (row: DeadlineRow): string =>
    t(...(TYPE_LABELS[getDisplayType(row)] ?? ['', row.type]));

  const columns: Column<DeadlineRow>[] = [
    {
      title: (
        <StyledCheckbox
          checked={visibleRows.length > 0 && selected.size === visibleRows.length}
          dash={selected.size > 0 && selected.size < visibleRows.length}
          onChange={toggleSelectAll}
          style={{ padding: 8, margin: -8 }}
        />
      ),
      field: 'id',
      sorting: false,
      width: '40px',
      cellStyle: { padding: '0 8px', width: '40px' },
      headerStyle: { padding: '0 8px', width: '40px' },
      render: (row) => (
        <div onClick={(e) => e.stopPropagation()}>
          <StyledCheckbox
            checked={selected.has(row.id)}
            onChange={() => toggleSelect(row.id)}
            style={{ padding: 8, margin: -8 }}
          />
        </div>
      ),
    },
    {
      title: t('deadlinesMessages.descriptionColumn', 'Beskrivning'),
      field: 'description',
    },
    {
      title: t('deadlinesMessages.typeColumn', 'Typ'),
      field: 'type',
      render: (row) => <TypeBadge type={getDisplayType(row)} label={getTypeLabel(row)} />,
    },
    {
      title: t('deadlinesMessages.deadlineColumn', 'Deadline'),
      field: 'deadline',
      render: (row) => moment(row.deadline).format('D MMMM YYYY'),
    },
    {
      title: t('deadlinesMessages.daysLeftColumn', 'Dagar kvar'),
      field: 'daysLeft',
      // This — not the pre-sorted `data` — is what orders the rendered table, because
      // defaultOrderBy points at this column.
      customSort: compareDeadlineRows,
      render: (row) => <DaysLeftBadge daysLeft={row.daysLeft} />,
    },
    {
      title: '',
      field: 'id',
      sorting: false,
      width: '50px',
      cellStyle: { padding: '0 8px' },
      render: (row) => (
        <button
          className={`${styles.dismissBtn} p-1 rounded hover:bg-gray-200`}
          title={t('deadlines.dismiss', 'Markera som klar')}
          onClick={(e) => {
            e.stopPropagation();
            handleDismiss(row);
          }}
        >
          <CheckIcon style={{ fontSize: 16, color: 'var(--gray-600)' }} />
        </button>
      ),
    },
  ];

  return (
    <>
      {/* An empty table and a failed request look identical, so without this the page reassures the
          user they have nothing due while the request is 500ing — the counters below read 0/0/0 too.
          role="alert" because this contradicts what the rest of the page is showing. */}
      {loadFailed && (
        <div className={styles.loadError} role="alert">
          <WarningAmberIcon style={{ fontSize: 18 }} />
          <span className={styles.loadErrorText}>
            {t(
              'deadlinesMessages.loadFailed',
              'Dina deadlines kunde inte hämtas. Listan visas därför tom — det betyder inte att du saknar deadlines.'
            )}
          </span>
          <button type="button" className={styles.loadErrorRetry} onClick={loadDeadlines}>
            {t('deadlinesMessages.retry', 'Försök igen')}
          </button>
        </div>
      )}

      <div className={styles.metricsGrid}>
        <Card className={styles.metricCard}>
          <div className={styles.metricCardText}>
            <p className={styles.metricCardTitle}>{t('deadlinesMessages.metrics.overdue', 'Försenade')}</p>
            <p className={styles.metricCardNumber} style={urgencyStyle('red')}>{overdueCount}</p>
            <p className={styles.metricCardSubtitle}>{t('deadlinesMessages.metrics.overdueSubtitle', 'Passerat deadline')}</p>
          </div>
          <div className={styles.metricCardIcon} style={urgencyStyle('red')}>
            <WarningAmberIcon style={{ fontSize: 16 }} />
          </div>
        </Card>

        <Card className={styles.metricCard}>
          <div className={styles.metricCardText}>
            <p className={styles.metricCardTitle}>{t('deadlinesMessages.metrics.soon', 'Förfaller snart')}</p>
            <p className={styles.metricCardNumber} style={urgencyStyle('yellow')}>{soonCount}</p>
            <p className={styles.metricCardSubtitle}>{t('deadlinesMessages.metrics.soonSubtitle', 'Inom 7 dagar')}</p>
          </div>
          <div className={styles.metricCardIcon} style={urgencyStyle('yellow')}>
            <ScheduleIcon style={{ fontSize: 16 }} />
          </div>
        </Card>

        <Card className={styles.metricCard}>
          <div className={styles.metricCardText}>
            <p className={styles.metricCardTitle}>{t('deadlinesMessages.metrics.upcoming', 'Kommande')}</p>
            <p className={styles.metricCardNumber} style={urgencyStyle('gray')}>{upcomingCount}</p>
            <p className={styles.metricCardSubtitle}>{t('deadlinesMessages.metrics.upcomingSubtitle', 'Mer än 7 dagar')}</p>
          </div>
          <div className={styles.metricCardIcon} style={urgencyStyle('gray')}>
            <CalendarMonthIcon style={{ fontSize: 16 }} />
          </div>
        </Card>
      </div>

      <TodoCalendar rows={typeFilteredRows} selectedDates={selectedDates} onToggleDate={toggleDate} />

      {/* Picker: click a type to add it as an active-filter chip below (no highlight state — the
          chips are the single source of truth for what's active). */}
      <div className={styles.typeFilters}>
        {typeOptions.map(opt => (
          <FilterPill
            key={opt.value}
            onClick={() => toggleTypeFilter(opt.value)}
            dotStyle={typeDotStyle(opt.value)}
            label={opt.label}
          />
        ))}
      </div>

      {/* Unified active-filter chip bar: selected types + selected dates share one uniform chip
          style (matching the picker pills); each removable, plus a clear-all. */}
      {(typeFilters.size > 0 || selectedDates.size > 0) && (
        <div className={styles.filterChips}>
          {/* Each ✕ names the filter it removes. They used to share one "Rensa filter" label, so a
              screen-reader user landed on a row of identically-named buttons with no way to tell
              which chip each one belonged to. */}
          {Array.from(typeFilters).map((type) => (
            <span key={`type-${type}`} className={styles.filterChip}>
              <span className={styles.typeDot} style={typeDotStyle(type)} />
              {t(...TYPE_LABELS[type])}
              <button
                type="button"
                className={styles.filterChipClose}
                aria-label={t('calendar.clearFilterFor', 'Rensa filter: {{name}}', { name: t(...TYPE_LABELS[type]) })}
                onClick={() => toggleTypeFilter(type)}
              >
                ✕
              </button>
            </span>
          ))}
          {Array.from(selectedDates).sort().map((iso) => (
            <span key={`date-${iso}`} className={styles.filterChip}>
              {/* The visible chip stays short; the accessible name carries the year, which "14 jul"
                  alone leaves ambiguous once the range spans a year boundary. */}
              {moment(iso).format('D MMM')}
              <button
                type="button"
                className={styles.filterChipClose}
                aria-label={t('calendar.clearFilterFor', 'Rensa filter: {{name}}', { name: moment(iso, 'YYYY-MM-DD').format('D MMMM YYYY') })}
                onClick={() => toggleDate(iso)}
              >
                ✕
              </button>
            </span>
          ))}
          <button type="button" className={styles.clearAll} onClick={clearAllFilters}>
            {t('calendar.clearAll', 'Rensa alla')}
          </button>
        </div>
      )}

      <div className={`${styles.deadlinesTable} ${selected.size > 0 ? styles.withActionBar : ''}`}>
        <DataTable
          isLoading={isLoading}
          columns={columns}
          data={visibleRows}
          pageSize={15}
          sorting
          thirdSortClick={false}
          defaultOrderBy={4}
          defaultOrderDirection="asc"
          onRowClick={(_, row) => {
            if (row) history.push(`${orgUrl}${row.href}`);
          }}
        />
      </div>

      <FloatingActionBar
        count={selected.size}
        countLabel={t('deadlines.selected', 'valda')}
        onClose={() => setSelected(new Set())}
      >
        <FloatingActionBarButton
          icon={<CheckIcon style={{ fontSize: 14 }} />}
          label={t('deadlines.bulkDismiss', 'Markera som klar')}
          onClick={handleBulkDismiss}
        />
      </FloatingActionBar>
    </>
  );
};

export default DeadlinesTab;
